import { ConfigProvider, DatePicker, Form } from 'antd';
import { ColProps } from 'antd/lib/col';
import { Rule } from 'antd/lib/form';
import csCZ from 'antd/lib/locale/cs_CZ';
import dayjs from 'dayjs';
import 'dayjs/locale/cs';
import React, { ReactNode, useContext, useRef } from 'react';

import Paragraph from 'components/atoms/Paragraph';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';
import InfoTooltip from 'components/molecules/InfoTooltip';

import getLanguageObject from 'utils/getLanguageObject';

// https://ant.design/components/date-picker/#API
const { Item } = Form;

interface IDatepicker {
    label?: string;
    name: string;
    initialValue?: string | dayjs.Dayjs;
    rules?: Rule[];
    extramessage?: string;
    onChange?: (value: dayjs.Dayjs, dateString: string | string[]) => void;
    placeholder?: string;
    notRequired?: boolean;
    disabledDate?: (date: dayjs.Dayjs) => boolean;
    defaultPickerValue?: dayjs.Dayjs;
    defaultValue?: dayjs.Dayjs;
    format?: string | ((value: dayjs.Dayjs) => string);
    style?: React.CSSProperties;
    showTime?: any;
    /** Urcuje sirku labelu */
    labelCol?: ColProps;
    disabled?: boolean;
    disabledTime?: (current: dayjs.Dayjs | null) => {
        disabledHours?: () => number[];
        disabledMinutes?: () => number[];
        disabledSeconds?: () => number[];
    };
    infoTooltipText?: string | ReactNode;
    hide?: boolean;
    onOpenChange?: () => void;
    className?: string;
    value?: dayjs.Dayjs | null | undefined;
}

const DatePickerComponent: React.FC<IDatepicker> = props => {
    const { language } = useContext(Context);
    const {
        label,
        name,
        initialValue,
        rules = [
            {
                required: true,
                message: <TranslateText>NEVALIDNI_POVINNA_POLOZKA</TranslateText>
            }
        ],
        notRequired,
        extramessage = '',
        infoTooltipText = '',
        onChange,
        placeholder = 'VYBERTE_PLACEHOLDER',
        disabledDate,
        defaultPickerValue,
        defaultValue,
        showTime,
        format = ['DD.MM.YYYY', 'D.M.YYYY'],
        style = {
            width: '100%'
        },
        labelCol,
        disabled = false,
        hide,
        onOpenChange,
        className,
        value,
        ...rest
    } = props;
    // ********* Foramt datumu z 1.1.2020 na 01.01.2020 ********* //
    const datePickerRef = useRef<any>(null);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const reformatDate = (event: any) => {
        const strippedInput: string[] = event.target.value.split(/\.|\/|-/, 3);
        if (strippedInput.length === 3 && strippedInput[0].length && strippedInput[1].length) {
            const formatedInput = strippedInput.map((e, i) => (i < 2 ? (e.length < 2 ? `0${e}` : e) : e));
            const newInput = formatedInput.join('.');
            event.target.value = newInput;
        }
    };

    React.useEffect(() => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const datePicker = datePickerRef.current.nativeElement;
        const dateInput = datePicker.children[0]?.children[0];
        dateInput?.addEventListener('blur', reformatDate);
        if (dateInput) dateInput.maxLength = 10;

        return () => {
            dateInput?.removeEventListener('blur', reformatDate);
        };
    });
    // ******************************************************** //
    return (
        <ConfigProvider locale={language === 'cz' ? csCZ : undefined}>
            <Item
                label={
                    label ? (
                        <>
                            <Paragraph text={label} strong={true} />{' '}
                            {infoTooltipText && <InfoTooltip title={infoTooltipText} />}{' '}
                        </>
                    ) : null
                }
                name={name}
                initialValue={initialValue}
                rules={notRequired ? undefined : rules}
                extra={<Paragraph text={extramessage} className="extramessage-text" />}
                hidden={hide}
                labelCol={labelCol}
                className={className}
            >
                <DatePicker
                    id={name}
                    ref={datePickerRef}
                    onOpenChange={onOpenChange}
                    onChange={onChange}
                    placeholder={getLanguageObject(language)[placeholder] || placeholder}
                    disabledDate={disabledDate}
                    showTime={
                        showTime
                            ? {
                                  ...showTime,
                                  format: 'HH:mm'
                              }
                            : undefined
                    }
                    defaultPickerValue={defaultPickerValue}
                    defaultValue={defaultValue}
                    format={format}
                    style={{
                        ...style,
                        borderRadius: 6
                    }}
                    disabled={disabled}
                    value={value}
                    {...rest}
                />
            </Item>
        </ConfigProvider>
    );
};

export default DatePickerComponent;
